import React, { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, X, Upload, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { title: "", title_zh: "", slug: "", excerpt: "", excerpt_zh: "", content: "", content_zh: "", cover_image: "", author: "TULSI Team", is_published: true };

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/blog/${editing.id}`, form);
        toast.success("Post updated");
      } else {
        await api.post("/blog", form);
        toast.success("Post created");
      }
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/blog/${id}`);
    toast.success("Deleted"); load();
  };

  const togglePublish = async (p) => {
    await api.put(`/blog/${p.id}`, { ...p, is_published: !p.is_published });
    load();
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backend = process.env.REACT_APP_BACKEND_URL;
      setForm({ ...form, cover_image: `${backend}${res.data.url}` });
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
  };

  return (
    <div data-testid="blog-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif-display text-4xl text-gold-gradient">Blog / Stories</h1>
          <p className="text-sm text-[#D1C7C9]">Write articles, recipes, and behind-the-kitchen stories with images</p>
        </div>
        <button onClick={openCreate} className="btn-saffron" data-testid="add-post-btn"><Plus size={14} /> New Post</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {posts.map((p) => (
          <div key={p.id} className="bg-[#1A1A1A] border border-[#333] rounded-xl p-4 flex gap-4" data-testid={`admin-post-${p.id}`}>
            {p.cover_image ? <img src={p.cover_image} alt="" className="w-24 h-24 rounded-md object-cover" /> : <div className="w-24 h-24 rounded-md bg-[#2A1116] flex items-center justify-center text-3xl">📖</div>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium truncate text-white">{p.title}</h3>
                {p.is_published ? <span className="text-[10px] text-green-400 uppercase tracking-widest">Live</span> : <span className="text-[10px] text-yellow-400 uppercase tracking-widest">Draft</span>}
              </div>
              <p className="text-xs text-[#D1C7C9] truncate">{p.title_zh || "—"}</p>
              <p className="text-[10px] text-[#888] mt-1">{new Date(p.created_at).toLocaleDateString()} · /blog/{p.slug}</p>
              <div className="flex gap-3 mt-3">
                <button onClick={() => openEdit(p)} className="text-xs text-[#E69138]" data-testid={`edit-post-${p.id}`}><Pencil size={11} className="inline" /> Edit</button>
                <button onClick={() => togglePublish(p)} className="text-xs text-blue-400" data-testid={`toggle-post-${p.id}`}>{p.is_published ? <><EyeOff size={11} className="inline" /> Unpublish</> : <><Eye size={11} className="inline" /> Publish</>}</button>
                <button onClick={() => del(p.id)} className="text-xs text-red-400" data-testid={`delete-post-${p.id}`}><Trash2 size={11} className="inline" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="text-[#D1C7C9] col-span-full">No posts yet. Click "New Post" to write your first story.</p>}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-[#1A1A1A] border border-[#333] rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="blog-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif-display text-2xl text-gold-gradient">{editing ? "Edit Post" : "New Post"}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X size={20} className="text-[#D1C7C9]" /></button>
            </div>
            <div className="space-y-3">
              <Input label="Title (English)" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testId="form-title" />
              <Input label="Title (中文)" value={form.title_zh} onChange={(v) => setForm({ ...form, title_zh: v })} testId="form-title-zh" />
              <Input label="URL slug (optional — auto-generated)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="form-slug" />
              <Input label="Author" value={form.author} onChange={(v) => setForm({ ...form, author: v })} testId="form-author" />
              <div>
                <label className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">Cover Image</label>
                {form.cover_image && <img src={form.cover_image} alt="" className="w-40 h-24 rounded-md object-cover mb-2" />}
                <div className="flex gap-2">
                  <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="Image URL or upload"
                    className="flex-1 bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138]" data-testid="form-cover-url" />
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} />
                  <button type="button" onClick={() => fileRef.current?.click()} className="btn-outline-gold text-xs px-3" data-testid="form-cover-upload"><Upload size={12} /> Upload</button>
                </div>
              </div>
              <TextArea label="Excerpt (short summary, English)" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} rows={2} testId="form-excerpt" />
              <TextArea label="Excerpt (中文)" value={form.excerpt_zh} onChange={(v) => setForm({ ...form, excerpt_zh: v })} rows={2} testId="form-excerpt-zh" />
              <TextArea label="Full content (English) — line breaks preserved" value={form.content} onChange={(v) => setForm({ ...form, content: v })} rows={10} testId="form-content" />
              <TextArea label="Full content (中文)" value={form.content_zh} onChange={(v) => setForm({ ...form, content_zh: v })} rows={10} testId="form-content-zh" />
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-[#0A0A0A]">
                <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="accent-[#E69138]" data-testid="form-published" />
                <span className="text-sm text-[#D1C7C9]">Published (visible on public site)</span>
              </label>
            </div>
            <div className="flex gap-2 mt-6">
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline-gold flex-1 justify-center">Cancel</button>
              <button type="submit" className="btn-saffron flex-1 justify-center" data-testid="form-save-btn">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, required, testId }) {
  return <label className="block"><span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">{label}</span>
    <input required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138]" /></label>;
}
function TextArea({ label, value, onChange, rows, testId }) {
  return <label className="block"><span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">{label}</span>
    <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138] resize-y font-mono" /></label>;
}
